import React from 'react'
import { useSearchParams } from 'react-router'
import type { FollowMeSharedBet } from '@core/Areas/FollowMe/types'
import { getFollowMeSharedBetByPostId } from '@core/Areas/FollowMe/helpers/api'
import type { BetOptions } from '@core/Areas/FollowMe/Components/ProfileDisplay/Components/Bets/types'
import {
  IncomingBetOpenPostIdSearchParam,
  IncomingBetViewPostIdSearchParam,
} from '@core/Areas/FollowMe/Components/ProfileDisplay/constants'

export const useIncomingSharedBet = ({
  selectedBetsOption,
  setBetsOption,
}: {
  selectedBetsOption: BetOptions
  setBetsOption(tab: BetOptions): void
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [incomingShare, setIncomingShare] = React.useState<FollowMeSharedBet | null>(null)
  const [isViewOnly, setIsViewOnly] = React.useState<boolean>(false)

  const incomingPostId = searchParams.get(IncomingBetOpenPostIdSearchParam)
  const incomingViewPostId = searchParams.get(IncomingBetViewPostIdSearchParam)

  const cleanSearchParams = React.useCallback(() => {
    setSearchParams(
      prev => {
        prev.delete(IncomingBetOpenPostIdSearchParam)
        prev.delete(IncomingBetViewPostIdSearchParam)
        return prev
      },
      { replace: true }
    )
  }, [setSearchParams])

  // Reset share on options change
  React.useEffect(() => {
    setIncomingShare(null)
    setIsViewOnly(false)
  }, [selectedBetsOption])

  // Handle Incoming Shares w/ Auto Open Quickbet by Default
  React.useEffect(() => {
    if (!incomingPostId) return
    const postId = incomingPostId
    cleanSearchParams()

    getFollowMeSharedBetByPostId(postId).then(sharedBet => {
      setBetsOption(getProfileTipsOption(sharedBet))
      setIncomingShare(sharedBet)
    })
  }, [incomingPostId, setIncomingShare, setBetsOption, cleanSearchParams])

  // Handle Incoming View Shared Bet
  React.useEffect(() => {
    if (!incomingViewPostId) return
    const postId = incomingViewPostId
    cleanSearchParams()

    getFollowMeSharedBetByPostId(postId).then(sharedBet => {
      setBetsOption(getProfileTipsOption(sharedBet))
      setIncomingShare(sharedBet)
      setIsViewOnly(true)
    })
  }, [incomingViewPostId, setIncomingShare, setBetsOption, cleanSearchParams])

  const shouldAutoOpenQuickbet = !!(incomingShare && !incomingShare.isResulted && !isViewOnly)

  return { incomingShare, shouldAutoOpenQuickbet }
}

// =============
// Local Helpers
// =============

function getProfileTipsOption(sharedBet: FollowMeSharedBet): BetOptions {
  return sharedBet.isResulted ? 'Resulted Shares' : 'Pending Shares'
}
